import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { BackupHistoryPage, BackupKeyInfo, BackupsOverview, RestoreItem } from '@shared/backups';

// Data for the Backups and Backup keys sections. The server refreshes these keys over the live stream whenever a
// backup, key or restore changes; while something runs the screens also poll, so progress moves smoothly.

export const BASE = '/admin/backups';
export const overviewKey = ['admin', 'backups', 'overview'] as const;
export const historyKey = ['admin', 'backups', 'history'] as const;
export const restorableKey = ['admin', 'backups', 'restorable'] as const;
export const keysKey = ['admin', 'keys'] as const;
export const restoreKey = (id: string) => ['admin', 'backups', 'restore', id] as const;

const POLL_MS = 1500;

export function busyOverview(o: BackupsOverview | undefined): boolean {
  return !!o && (o.running.length > 0 || o.restore?.status === 'running');
}

export function useOverview() {
  return useQuery({
    queryKey: overviewKey,
    queryFn: ({ signal }) => api.get<BackupsOverview>(`${BASE}/overview`, { signal }),
    staleTime: 15_000,
    refetchInterval: (q) => (busyOverview(q.state.data) ? POLL_MS : false),
  });
}

const PAGE = 25;

export function useHistory(polling: boolean) {
  return useInfiniteQuery({
    queryKey: historyKey,
    queryFn: ({ pageParam, signal }) => api.get<BackupHistoryPage>(`${BASE}/history`, { signal, query: { before: pageParam, limit: PAGE } }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
    staleTime: 15_000,
    refetchInterval: polling ? POLL_MS : false,
  });
}

/** Every backup a restore can use, newest first, a page at a time. */
export function useRestorable() {
  return useInfiniteQuery({
    queryKey: restorableKey,
    queryFn: ({ pageParam, signal }) => api.get<BackupHistoryPage>(`${BASE}/restorable`, { signal, query: { before: pageParam, limit: PAGE } }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
    staleTime: 15_000,
  });
}

export function useKeys() {
  return useQuery({
    queryKey: keysKey,
    queryFn: ({ signal }) => api.get<BackupKeyInfo[]>(`${BASE}/keys`, { signal }),
    staleTime: 30_000,
  });
}

export function fetchRestore(id: string, signal?: AbortSignal): Promise<RestoreItem> {
  return api.get<RestoreItem>(`${BASE}/restores/${id}`, { signal });
}

/** Hands a same-origin or presigned link to the browser's own download manager. */
export function downloadFrom(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  a.download = '';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Saves text made in the browser as a file. */
export function saveText(fileName: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/x-pem-file' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
